import React, { useState, useEffect } from 'react';
import { Table, Spin, message, Input, Button, Row, Col, Space, Alert, DatePicker } from 'antd';
import { SearchOutlined, CalendarOutlined, UserOutlined, ShopOutlined } from '@ant-design/icons';
import { GET } from '../../utils/apiCalls'; 
import API from '../../config/api';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';

interface Branch {
  id: string;
  bname: string;
  fulladdress: string;
}

interface BranchReportItem {
  key: string;
  branchId: string;
  branchName: string;
  branchAddress: string;
  totalAmount: number;
  totalOrders: number;
}

const BranchReport: React.FC = () => {
  const { user } = useSelector((state: any) => state.User);
  const format = 'YYYY-MM-DD HH:mm:ss';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<BranchReportItem[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('Today');
  const [displayDate, setDisplayDate] = useState<Date>(new Date());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [date, setDate] = useState({
    key: 'Today',
    from_date: moment().startOf('day').format(format),
    to_date: moment().endOf('day').format(format),
  });

  const fetchReportData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      let filter1 = `?page=${pagination.current}`;
      let filter2 = `&limit=${pagination.pageSize}`;
      let filter3 = searchQuery ? `&query=${searchQuery}` : '';
      let filter4 = date?.key ? `&from_date=${date?.from_date}&to_date=${date?.to_date}` : '';
      
      let url = `${API.BRANCHES_LIST}${filter1}${filter2}${filter3}${filter4}`;

      if(!user.staff?.staffAccess?.includes("administrator")){
        url = url + `&branchId=${user?.staff?.companyid}`;
      }
      
      const response: any = await GET(url,{});
      
      if (response?.status && response?.data) {
        const formattedData = response.data.map((item: any) => {
          return {
            key: item.CompanyMaster.id,
            branchId: item.CompanyMaster.id,
            branchName: item.CompanyMaster.bname,
            branchAddress: item.CompanyMaster.fulladdress,
            totalAmount: parseFloat(String(item.totalSales)) || 0,
            totalOrders: item.totalOrders || 0,
          };
        });
        
        setReportData(formattedData);
        setPagination(prev => ({
          ...prev,
          total: response.total || formattedData.length
        }));
      } else {
        setError(response?.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Oops. Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [pagination.current, pagination.pageSize, searchQuery, date]);

  const handleDateRangeChange = (range: string) => {
    setSelectedDateRange(range);
    let fromDate: moment.Moment;
    let toDate: moment.Moment;

    switch (range) {
      case 'Today':
        fromDate = moment().startOf('day');
        toDate = moment().endOf('day');
        break;
      case 'Yesterday':
        fromDate = moment().subtract(1, 'days').startOf('day');
        toDate = moment().subtract(1, 'days').endOf('day');
        break;
      case 'This month':
        fromDate = moment().startOf('month');
        toDate = moment().endOf('month');
        break;
      case 'Last month':
        fromDate = moment().subtract(1, 'months').startOf('month');
        toDate = moment().subtract(1, 'months').endOf('month');
        break;
      default:
        fromDate = moment().startOf('day');
        toDate = moment().endOf('day');
    }

    setDate({
      key: range,
      from_date: fromDate.format(format),
      to_date: toDate.format(format)
    });
    setDisplayDate(fromDate.toDate());
  };

  const handleCustomDateChange = (selectedDate: moment.Moment | null) => {
    if (selectedDate) {
      setSelectedDateRange('Custom');
      const fromDate = selectedDate.startOf('day');
      const toDate = selectedDate.endOf('day');
      
      setDate({
        key: 'Custom',
        from_date: fromDate.format(format),
        to_date: toDate.format(format)
      });
      setDisplayDate(fromDate.toDate());
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
  };

  const columns = [
    {
      title: 'Branch Name',
      dataIndex: 'branchName',
      key: 'branchName',
      render: (text: string) => (
        <div className="flex items-center">
          <ShopOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '8px' }} />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'branchAddress',
      key: 'branchAddress',
      render: (text: string) => (
        <div className="flex items-center">
          <UserOutlined style={{ marginRight: '8px' }} />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Total Orders',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      sorter: (a: BranchReportItem, b: BranchReportItem) => a.totalOrders - b.totalOrders,
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `₹${amount.toFixed(2)}`,
      sorter: (a: BranchReportItem, b: BranchReportItem) => a.totalAmount - b.totalAmount,
    },
  ];

  return (
    <div className="p-4">
      <Row justify="space-between" align="middle" className="mb-4">
        <Col>
          <h1 className="text-2xl font-bold">{user?.staff?.staffAccess?.includes("administrator") ? "Branches" : "Branch Report"}</h1>
        </Col>
        <Col>
          <UserOutlined style={{ fontSize: '24px' }} />
        </Col>
      </Row>

      

      <Space className="mb-4">
        <Button type={selectedDateRange === 'Today' ? 'primary' : 'default'} onClick={() => handleDateRangeChange('Today')}>Today</Button>
        <Button type={selectedDateRange === 'Yesterday' ? 'primary' : 'default'} onClick={() => handleDateRangeChange('Yesterday')}>Yesterday</Button>
        <Button type={selectedDateRange === 'This month' ? 'primary' : 'default'} onClick={() => handleDateRangeChange('This month')}>This month</Button>
        <Button type={selectedDateRange === 'Last month' ? 'primary' : 'default'} onClick={() => handleDateRangeChange('Last month')}>Last month</Button>
        <DatePicker
          onChange={handleCustomDateChange}
          format="YYYY-MM-DD"
          className="ml-2"
          placeholder="Select date"
        />
      </Space>

      <p className="text-gray-600 mb-4">
        {reportData.length} branches on {displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </p>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          className="mb-4"
          action={
            <Button size="small" type="primary" onClick={fetchReportData}>
              Try Again
            </Button>
          }
        />
      )}

      <Spin spinning={loading}>
        <Table
          dataSource={reportData}
          columns={columns}
          rowKey="key"
          pagination={pagination}
          onChange={handleTableChange}
          className="shadow-sm"
        />
      </Spin>
    </div>
  );
};

export default BranchReport;